import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Public Pages
import Home from './pages/public/Home';
import Features from './pages/public/Features';
import About from './pages/public/About';
import Contact from './pages/public/Contact';
import Login from './pages/public/Login';
import SignUp from './pages/public/SignUp';
import PrivacyPolicy from './pages/public/PrivacyPolicy';

// Protected Pages
import Dashboard from './pages/protected/Dashboard';
import Expense from './pages/protected/Expense';
import UserSettings from './pages/protected/UserSettings';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import BrandingPage from './pages/admin/BrandingPage';
import UserRoles from './pages/admin/UserRoles';
import Customers from './pages/admin/Customers';
import PaymentOptions from './pages/admin/PaymentOptions';
import UsageReports from './pages/admin/UsageReports';
import Integrations from './pages/admin/Integrations';
import RecentActivity from './pages/admin/RecentActivity';


// Layout Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Auth Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';

import './App.css';
import { BrandingProvider } from './contexts/BrandingContext';

const App = () => {
    return (
        <ThemeProvider>
            <BrandingProvider>
                <AuthProvider>
                    <Router>
                        <MainContent />
                    </Router>
                </AuthProvider>
            </BrandingProvider>
        </ThemeProvider>
    );
};

const MainContent = () => {
    const { user, loading } = useAuth();
    const [isSidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        // Optional: Add listeners or effects that depend on user state
    }, [user]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
            <Toaster position="top-right" reverseOrder={false} />
            <Navbar isSidebarOpen={isSidebarOpen} setSidebarOpen={setSidebarOpen} />
            <main className="flex-grow">
                <AnimatePresence mode="wait">
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<Home />} />
                        <Route path="/features" element={<Features />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
                        <Route path="/signup" element={user ? <Navigate to="/dashboard" /> : <SignUp />} /> {/* Add SignUp Route */}
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                        {/* Protected Routes */}
                        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                        <Route path="/expenses" element={<ProtectedRoute><Expense /></ProtectedRoute>} />
                        <Route path="/settings" element={<ProtectedRoute><UserSettings /></ProtectedRoute>} />

                        {/* Admin Routes */}
                        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                        <Route path="/admin/branding" element={<AdminRoute><BrandingPage /></AdminRoute>} />
                        <Route path="/admin/roles" element={<AdminRoute><UserRoles /></AdminRoute>} />
                        <Route path="/admin/customers" element={<AdminRoute><Customers /></AdminRoute>} />
                        <Route path="/admin/payments" element={<AdminRoute><PaymentOptions /></AdminRoute>} />
                        <Route path="/admin/reports" element={<AdminRoute><UsageReports /></AdminRoute>} />
                        <Route path="/admin/integrations" element={<AdminRoute><Integrations /></AdminRoute>} />
                        <Route path="/admin/activity" element={<AdminRoute><RecentActivity /></AdminRoute>} />

                        {/* Fallback Route */}
                        <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                </AnimatePresence>
            </main>
            <Footer />
        </div>
    );
};

export default App;